import { addDays, differenceInCalendarDays, format } from 'date-fns'
import { parseCalendarDate, toCalendarDate } from '@/lib/calendar-date'
import type { ApiRequestError, OwnerBlockRecord } from '@/api/client'
import { blockKey, type CalendarBlockLike } from '@/components/calendar/block-bars'

/**
 * The pure parts of the block panels: what `?open=` can hold for a block, the
 * overlap note, the cc field and the words for each way a write can fail. Kept
 * out of the components so each can be tested without rendering a sheet.
 */

// What `?open=` holds for a block: `block:owner:<block id>`, `block:hold:<id>`
// or `block-new:<first night>`.
const OWNER_OPEN_PREFIX = 'block:owner:'
const HOLD_OPEN_PREFIX = 'block:hold:'
const NEW_OPEN_PREFIX = 'block-new:'

export function ownerBlockOpenValue(id: string): string {
  return `${OWNER_OPEN_PREFIX}${id}`
}
export function holdOpenValue(id: string): string {
  return `${HOLD_OPEN_PREFIX}${id}`
}
/**
 * `block-new:<first night>`, or `block-new:<first night>:<end>` for a range
 * dragged on the grid (`end` is the exclusive checkout day).
 */
export function newBlockOpenValue(start: string, end?: string): string {
  return end ? `${NEW_OPEN_PREFIX}${start}:${end}` : `${NEW_OPEN_PREFIX}${start}`
}

export type BlockOpen =
  | { kind: 'owner'; id: string }
  | { kind: 'hold'; id: string }
  | { kind: 'new'; start: string; end?: string }

export function parseBlockOpen(raw: string | null): BlockOpen | null {
  if (!raw) return null
  if (raw.startsWith(OWNER_OPEN_PREFIX)) {
    const id = raw.slice(OWNER_OPEN_PREFIX.length)
    return id ? { kind: 'owner', id } : null
  }
  if (raw.startsWith(HOLD_OPEN_PREFIX)) {
    const id = raw.slice(HOLD_OPEN_PREFIX.length)
    return id ? { kind: 'hold', id } : null
  }
  if (raw.startsWith(NEW_OPEN_PREFIX)) {
    const [start, end] = raw.slice(NEW_OPEN_PREFIX.length).split(':')
    if (!parseCalendarDate(start)) return null
    // An end that does not leave at least one night is ignored, not an error:
    // the form opens on one night.
    return end && nightsBetween(start, end) !== null
      ? { kind: 'new', start, end }
      : { kind: 'new', start }
  }
  return null
}

export const BLOCK_CHECKIN_TIME_LABEL = '4:00 PM'
export const BLOCK_CHECKOUT_TIME_LABEL = '10:00 AM'

export function blockingWindowCopy(startLabel: string, endLabel: string): string {
  return `Guests are blocked out from ${BLOCK_CHECKIN_TIME_LABEL} on ${startLabel} to ${BLOCK_CHECKOUT_TIME_LABEL} on ${endLabel}.`
}

export function cleaningRequestCopy(endLabel: string | null): string {
  return `Request a cleaning for ${endLabel ?? 'the end date'} at ${BLOCK_CHECKOUT_TIME_LABEL}`
}

/** Nights in `[start, end)`, or null when either is not a date or the range is empty. */
export function nightsBetween(start: string, end: string): number | null {
  const first = parseCalendarDate(start)
  const free = parseCalendarDate(end)
  if (!first || !free) return null
  const nights = differenceInCalendarDays(free, first)
  return nights > 0 ? nights : null
}

/** The day after `day`: where a one-night block ends, and the form's starting end date. */
export function nextDay(day: string): string {
  const date = parseCalendarDate(day)
  return date ? toCalendarDate(addDays(date, 1)) : day
}

/** "Jun 12" */
export function shortDate(day: string): string {
  const date = parseCalendarDate(day)
  return date ? format(date, 'MMM d') : day
}

/** "Jun 12, 2027" */
export function longDate(day: string): string {
  const date = parseCalendarDate(day)
  return date ? format(date, 'MMM d, yyyy') : day
}

/** "Saturday, June 12" */
export function startHeading(day: string): string {
  const date = parseCalendarDate(day)
  return date ? format(date, 'EEEE, MMMM d') : day
}

/** "Sat, Jun 12 to Jun 19" */
export function rangeTitle(start: string, end: string): string {
  const first = parseCalendarDate(start)
  return `${first ? format(first, 'EEE, MMM d') : start} to ${shortDate(end)}`
}

// -- overlap note -------------------------------------------------------------

export interface OverlapSource {
  /** What it is, already worded: "Guest A reservation", "Owner block". */
  label: string
  start: string
  /** Exclusive. */
  end: string
}

/** How many overlaps the note lists before it says "and N more". */
export const OVERLAP_LIST_LIMIT = 4

/**
 * What a drafted range `[start, end)` runs over, for the information-only
 * note. Uplisting keeps every block and booking stacked, so this never stops a
 * submit.
 */
export function findOverlaps(start: string, end: string, sources: OverlapSource[]): string[] {
  return sources
    .filter(source => source.start < end && start < source.end)
    .sort((a, b) => a.start.localeCompare(b.start) || a.label.localeCompare(b.label))
    .map(source => `${source.label}, ${shortDate(source.start)} to ${shortDate(source.end)}`)
}

export function statusOf(block: OwnerBlockRecord): {
  label: string
  variant: 'mint' | 'danger' | 'amber'
} {
  switch (block.sync_status) {
    case 'deleting':
      return { label: 'Removing', variant: 'amber' }
    case 'failed':
      return block.ambiguous
        ? { label: 'Uncertain', variant: 'danger' }
        : { label: 'Sync failed', variant: 'danger' }
    case 'pending':
      return { label: 'Syncing', variant: 'amber' }
    default:
      return block.has_conflict
        ? { label: 'Conflict', variant: 'amber' }
        : { label: 'Blocked', variant: 'mint' }
  }
}

// -- stacked blocks -----------------------------------------------------------

/** The blocks that share at least one night with `block`, each with the nights they share `[from, to)`. */
export function blocksOverlapping(
  block: CalendarBlockLike,
  pool: CalendarBlockLike[]
): { block: CalendarBlockLike; from: string; to: string }[] {
  const self = blockKey(block)
  return pool
    .filter(
      other =>
        blockKey(other) !== self &&
        other.start < block.end_exclusive &&
        block.start < other.end_exclusive
    )
    .map(other => ({
      block: other,
      from: other.start > block.start ? other.start : block.start,
      to: other.end_exclusive < block.end_exclusive ? other.end_exclusive : block.end_exclusive,
    }))
    .sort(
      (a, b) => a.from.localeCompare(b.from) || blockKey(a.block).localeCompare(blockKey(b.block))
    )
}

/** What another block is called in a sentence: its reason, or what kind it is. */
function overlapName(block: CalendarBlockLike): string {
  if (block.kind === 'uplisting') return 'a block made in the Uplisting app'
  return block.note?.trim() || 'another owner block'
}

/**
 * The short phrase for a block's row in a list: "overlaps Family visit",
 * "overlaps 2 blocks", "inside Family visit". Null for a block nothing else
 * covers. Informational only.
 */
export function overlapPhrase(block: CalendarBlockLike, pool: CalendarBlockLike[]): string | null {
  const others = blocksOverlapping(block, pool).map(entry => entry.block)
  if (others.length === 0) return null
  const same = others.filter(
    other => other.start === block.start && other.end_exclusive === block.end_exclusive
  )
  const outer = others.filter(
    other =>
      other.start <= block.start &&
      other.end_exclusive >= block.end_exclusive &&
      !same.includes(other)
  )
  if (outer.length > 0) {
    return outer.length === 1 ? `inside ${overlapName(outer[0])}` : `inside ${outer.length} blocks`
  }
  if (same.length > 0) {
    return same.length === 1
      ? `same dates as ${overlapName(same[0])}`
      : `same dates as ${same.length} blocks`
  }
  return others.length === 1
    ? `overlaps ${overlapName(others[0])}`
    : `overlaps ${others.length} blocks`
}

/**
 * The one-line note on a block's own sheet: which other blocks cover some of
 * its nights, and which nights. Null when none does.
 */
export function overlapNote(block: CalendarBlockLike, pool: CalendarBlockLike[]): string | null {
  const entries = blocksOverlapping(block, pool)
  if (entries.length === 0) return null
  const listed = entries
    .slice(0, OVERLAP_LIST_LIMIT)
    .map(
      entry => `${overlapName(entry.block)} (${shortDate(entry.from)} to ${shortDate(entry.to)})`
    )
  const more = entries.length - listed.length
  return `Overlaps ${listed.join(', ')}${more > 0 ? `, and ${more} more` : ''}.`
}

// -- cc emails ----------------------------------------------------------------

/** Mirrors the portal's `OWNER_BLOCK_MAX_CC_EMAILS`. */
export const MAX_CC_EMAILS = 5
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** Splits the free-text cc field on commas and whitespace; lowercased, de-duplicated, bad ones reported. */
export function parseCcEmails(text: string): { emails: string[]; invalid: string[] } {
  const seen = new Set<string>()
  const emails: string[] = []
  const invalid: string[] = []
  for (const raw of text.split(/[,\s]+/)) {
    const candidate = raw.trim().toLowerCase()
    if (!candidate) continue
    if (!EMAIL_RE.test(candidate)) {
      invalid.push(raw.trim())
      continue
    }
    if (!seen.has(candidate)) {
      seen.add(candidate)
      emails.push(candidate)
    }
  }
  return { emails, invalid }
}

// -- what a failed write says ---------------------------------------------------

export type BlockWriteKind = 'create' | 'remove' | 'check'

interface ErrorLike {
  status?: number
  body?: unknown
  message?: string
}

function bodyField(error: ErrorLike, field: 'code' | 'message'): unknown {
  const body = error.body
  return body && typeof body === 'object' ? (body as Record<string, unknown>)[field] : undefined
}

function codeOf(error: ErrorLike): string | null {
  const code = bodyField(error, 'code')
  if (typeof code === 'string') return code
  // A 401/403 is thrown without its parsed body, but keeps the text.
  const match = /"code"\s*:\s*"([a-z_]+)"/.exec(error.message ?? '')
  return match ? match[1] : null
}

/** The code the server answered with, for a caller that has to react to one (a clashing block id). */
export function blockErrorCode(error: unknown): string | null {
  return error && typeof error === 'object' ? codeOf(error as ApiRequestError) : null
}

/** Plain next steps for a failed write, never the raw response text. */
export function describeBlockError(error: unknown, kind: BlockWriteKind): string {
  const err: ErrorLike = error && typeof error === 'object' ? (error as ErrorLike) : {}
  const code = codeOf(err)
  const status = err.status ?? (code === 'feature_not_enabled' ? 403 : undefined)

  if (code === 'feature_not_enabled') {
    return 'Owner blocks are switched off for this property. Switch them on in the Features tab, then try again.'
  }
  if (code === 'no_uplisting_mapping') {
    return kind === 'create'
      ? 'This property has no Uplisting listing mapped, so dates cannot be blocked. Map the listing first.'
      : 'This property has no Uplisting listing mapped, so Uplisting cannot be asked. Map the listing first.'
  }
  if (code === 'still_syncing') {
    return 'This block is already being removed in Uplisting. Try again in a moment.'
  }
  if (code === 'block_id_conflict') {
    return 'That attempt clashed with an earlier one. Press the button again to send it afresh.'
  }
  const serverMessage = bodyField(err, 'message')
  if ((status === 400 || status === 422) && typeof serverMessage === 'string' && serverMessage) {
    return serverMessage
  }
  if (status === 401 || status === 403) {
    return 'You are not allowed to do this. Sign in again, or ask for access.'
  }
  if (kind === 'check') return 'Couldn’t reach Uplisting to check. Try again in a moment.'
  if (kind === 'remove') {
    return 'Couldn’t remove this block: Uplisting did not respond. Try again - repeating it is safe. If it keeps failing, check the Uplisting connection on the property.'
  }
  return 'Couldn’t block these dates: Uplisting did not accept the request. Try again - the same request is safe to repeat. If it keeps failing, check the Uplisting connection on the property.'
}
